import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FilmCard from "@/components/feature/FilmCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import {
  CATEGORY_KEYS,
  categoryMeta,
  decadeOptions,
  filterFilms,
  GENRE_OPTIONS,
  getFilmsByCategory,
  paginate,
  regionOptions,
  sortOptions,
  totalPages,
} from "@/lib/catalog";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { type } = useParams();
  const category = type ? decodeURIComponent(type) : "";
  const meta = categoryMeta[category];
  const [region, setRegion] = useState("all");
  const [decade, setDecade] = useState("all");
  const [genre, setGenre] = useState("all");
  const [sort, setSort] = useState("popular");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  usePageMeta(
    meta
      ? `${meta.name}_在线日韩伦理片影视分类_日韩伦理片`
      : "分类未找到_日韩伦理片影视分类",
    meta
      ? `${meta.name}分类收录${meta.description}类日韩伦理影视作品，提供年代、地区、类型细分筛选与编辑解析，可按热度与评分排序浏览。`
      : "该分类暂未收录，可返回电影、电视剧或短片分类继续浏览日韩伦理影视作品。",
  );

  const results = useMemo(
    () => filterFilms({ category, region, decade, genre, sort }),
    [category, region, decade, genre, sort],
  );

  const pages = totalPages(results.length, PAGE_SIZE);
  const currentPage = Math.min(page, pages);
  const visible = paginate(results, currentPage, PAGE_SIZE);
  const categoryTotal = getFilmsByCategory(category).length;

  const resetFilters = () => {
    setRegion("all");
    setDecade("all");
    setGenre("all");
    setSort("popular");
    setPage(1);
  };

  const update = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  if (!meta) {
    return (
      <div className="mx-auto max-w-shell px-4 py-16 md:px-8">
        <Breadcrumbs items={[{ label: "分类" }]} />
        <EmptyState
          icon="ri-folder-line"
          title="这个分类不存在"
          description="我们目前只提供电影、电视剧与短片三个分类。你可以从下面的入口重新开始浏览。"
        >
          {CATEGORY_KEYS.map((key) => (
            <Link
              key={key}
              to={`/category/${encodeURIComponent(key)}`}
              className="flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-[12px] text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              {key}
            </Link>
          ))}
        </EmptyState>
      </div>
    );
  }

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-shell px-4 pb-8 pt-6 md:px-8 md:pb-10 md:pt-8">
          <Breadcrumbs items={[{ label: meta.name }]} />
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="font-label text-[11px] font-semibold uppercase tracking-[0.24em] text-accent-700">
                {meta.en}
              </p>
              <h1 className="mt-2 font-heading text-[32px] font-extrabold leading-tight tracking-tight text-foreground-950 md:text-[42px]">
                {meta.name}
              </h1>
              <p className="mt-3 text-[13px] leading-relaxed text-foreground-600 md:text-sm">
                {meta.longDescription}
              </p>
            </div>
            <div className="flex items-center gap-6 lg:pb-1">
              <div>
                <p className="font-heading text-2xl font-extrabold text-primary-800">{categoryTotal}</p>
                <p className="mt-0.5 font-label text-[10px] uppercase tracking-[0.16em] text-secondary-500">
                  收录作品
                </p>
              </div>
              <span className="h-10 w-[1px] bg-background-300" />
              <div>
                <p className="font-heading text-2xl font-extrabold text-primary-800">{results.length}</p>
                <p className="mt-0.5 font-label text-[10px] uppercase tracking-[0.16em] text-secondary-500">
                  当前筛选
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {CATEGORY_KEYS.map((key) => (
              <Link
                key={key}
                to={`/category/${encodeURIComponent(key)}`}
                className={`cursor-pointer rounded-full px-3.5 py-1.5 text-[12px] transition-colors ${
                  key === category
                    ? "bg-primary-800 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {key}
              </Link>
            ))}
              <span className="ml-1 font-label text-[11px] text-foreground-400">按分类切换</span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-shell px-4 pt-8 md:px-8">
        <div className="rounded-lg border border-background-200 bg-background-50">
          <button
            type="button"
            onClick={() => setFilterOpen((open) => !open)}
            className="flex w-full cursor-pointer items-center justify-between px-4 py-3.5 text-left md:hidden"
          >
            <span className="flex items-center gap-2 text-[13px] font-medium text-foreground-900">
              <i className="ri-equalizer-line text-primary-700" />
              筛选与排序
            </span>
            <span className="flex items-center gap-1 font-label text-[11px] text-foreground-500">
              {filterOpen ? "收起" : "展开"}
              <i className={`${filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-base`} />
            </span>
          </button>

          <div className={`${filterOpen ? "block" : "hidden"} border-t border-background-200 p-4 md:block md:border-t-0 md:p-5`}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <span className="font-label text-[10px] font-semibold uppercase tracking-[0.18em] text-secondary-500">
                  年代
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {decadeOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => update(setDecade)(option.key)}
                      className={`cursor-pointer rounded-full px-3 py-1.5 text-[12px] transition-colors ${
                        decade === option.key
                          ? "bg-primary-800 text-background-50"
                          : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <span className="font-label text-[10px] font-semibold uppercase tracking-[0.18em] text-secondary-500">
                  地区
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {regionOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => update(setRegion)(option.key)}
                      className={`cursor-pointer rounded-full px-3 py-1.5 text-[12px] transition-colors ${
                        region === option.key
                          ? "bg-primary-800 text-background-50"
                          : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 border-t border-background-200 pt-4 sm:flex-row sm:items-center">
              <label className="flex flex-1 items-center gap-2">
                <span className="shrink-0 font-label text-[11px] text-foreground-500">类型细分</span>
                <select
                  value={genre}
                  onChange={(event) => update(setGenre)(event.target.value)}
                  className="h-9 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 text-[12px] text-foreground-800 outline-none focus:border-primary-400"
                >
                  <option value="all">全部类型</option>
                  {GENRE_OPTIONS.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-1 items-center gap-2">
                <span className="shrink-0 font-label text-[11px] text-foreground-500">排序</span>
                <select
                  value={sort}
                  onChange={(event) => update(setSort)(event.target.value)}
                  className="h-9 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 text-[12px] text-foreground-800 outline-none focus:border-primary-400"
                >
                  {sortOptions.map((item) => (
                    <option key={item.key} value={item.key}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={resetFilters}
                className="flex h-9 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-background-300 px-3.5 text-[12px] text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-restart-line text-xs" />
                重置筛选
              </button>
            </div>
          </div>
        </div>

        <div className="mt-7 flex items-center justify-between">
          <p className="font-label text-[11px] uppercase tracking-[0.16em] text-secondary-500">
            {results.length} 个条目 · 第 {currentPage} / {pages} 页
          </p>
          <p className="hidden font-label text-[11px] text-foreground-400 sm:block">
            每页 {PAGE_SIZE} 条
          </p>
        </div>

        <div className="mt-4">
          {visible.length > 0 ? (
            <div className="grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="ri-filter-off-line"
              title="没有符合当前筛选的作品"
              description="试着放宽年代或地区的限制，或者直接重置全部条件，回到这个分类的完整列表。"
            >
              <button
                type="button"
                onClick={resetFilters}
                className="flex cursor-pointer items-center gap-1.5 rounded-md bg-primary-800 px-4 py-2 text-[12px] font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-restart-line text-xs" />
                重置筛选
              </button>
              <Link
                to="/specials"
                className="flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-[12px] text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                看看编辑专题
              </Link>
            </EmptyState>
          )}
        </div>

        <Pagination
          page={currentPage}
          total={pages}
          onChange={(nextPage) => {
            setPage(nextPage);
            window.scrollTo({ top: 240, behavior: "smooth" });
          }}
        />
      </section>
    </div>
  );
}